import React, { useEffect, useState } from 'react';
import '../../assets/css/overview-details.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import leftArrow from '../../assets/img/white-left-icon.svg';
import settingIcon from '../../assets/img/settings.svg';
import Header from '../../components/Header';
import DetailsTab from './overviewtabs/DetailsTab';
import ScopeOfWork from './overviewtabs/ScopeOfWork';
import SidebarModal from '../../components/SidebarModal';
import OrganizationForm from './forms/OrganizationForm';
import TeamTab from './overviewtabs/TeamTab';
import APIServices from '../../services/APIServices';
import MileStone from './overviewtabs/MileStone';
import appToast, { TOAST_TYPE } from '../../utils/AppToast';
import UpdatesTab from './overviewtabs/UpdatesTab';
import { fetchPermissions } from 'utils/fetchData';
import { selectLoader } from 'utils/Helpers';
import akarFile from '../../assets/img/akar-file.svg';

const ProjectOverview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: projectIdFromParams, projectWithContract } = useParams();
  const contractPrefillDetails = location.state?.contractPrefillDetails;
  const [modalOpen, setModalOpen] = useState(false);
  const [orgloading, setOrgLoading] = useState(false);
  const [error, setError] = useState('');
  const [addedOrgId, setOrgId] = useState('');
  const [currentTab, setCurrentTab] = useState(1);
  const [milestoneProgress, setMilestoneTab] = useState(null);
  const [fetchOrgs, setFetchOrgs] = useState(true);
  const [projectDetails, setprojectDetails] = useState('');
  const [editProjectDetails, setEditProjectDetails] = useState('');
  const [projectStatus, setProjectStatus] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [isInternalProject, setInternalProject] = useState(false);
  const [isWithContract, setWithContract] = useState(false);
  const [defaultTeam, setDefaultTeam] = useState([]);
  const [userPermissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [isContractUploaded, setContractUploaded] = useState(false);
  const [projectAccessStatus, setProjectAccessStatus] = useState(projectIdFromParams ? 'checking' : 'granted');
  const [prefetchedProjectDetails, setPrefetchedProjectDetails] = useState(null);
  const [contractPrefill, setContractPrefill] = useState({
    project: null,
    scope: '',
    milestones: [],
    contractFile: null
  });
  // State to track the completion status of each tab (default only first tab is available)
  const [completedTabs, setCompletedTabs] = useState({
    1: true, // First tab is initially available
    2: false,
    3: false,
    4: false,
    5: false
  });

  const canLoadProjectDependencies =
    !projectIdFromParams ||
    (projectAccessStatus === 'granted' && prefetchedProjectDetails?._id === projectIdFromParams);

  useEffect(() => {
    let isCancelled = false;

    const checkProjectAccess = async () => {
      if (!projectIdFromParams) {
        setProjectAccessStatus('granted');
        setPrefetchedProjectDetails(null);
        return;
      }

      setProjectAccessStatus('checking');
      setPrefetchedProjectDetails(null);

      try {
        const response = await APIServices.get(`/project/${projectIdFromParams}`);
        if (isCancelled) return;

        if (response?.projectDetail) {
          const projectDetail = response.projectDetail;
          setPrefetchedProjectDetails(projectDetail);
          setEditProjectDetails(projectDetail);
          setInternalProject(projectDetail.type === 'internal');
          setProjectAccessStatus('granted');
          return;
        }

        const message = response?.data?.message || response?.message || 'You do not have access to this project.';
        setProjectAccessStatus('denied');
        appToast(message, TOAST_TYPE.ERROR);
      } catch (accessError) {
        if (isCancelled) return;

        const message = accessError?.message || 'Unable to verify project access.';
        setProjectAccessStatus('denied');
        appToast(message, TOAST_TYPE.ERROR);
      }
    };

    checkProjectAccess();

    return () => {
      isCancelled = true;
    };
  }, [projectIdFromParams]);

  useEffect(() => {
    if (contractPrefillDetails) {
      setContractUploaded(true);
      setContractPrefill({
        project: contractPrefillDetails.projectDetails,
        scope: contractPrefillDetails.scope,
        milestones: contractPrefillDetails.milestones,
        contractFile: contractPrefillDetails.contractFile,
      });
      setInternalProject(contractPrefillDetails.isInternalProjectData);
      //  setMilestoneTab('add');
    }
  }, [contractPrefillDetails]);

  // Fetch organizations data
  const fetchOrganizations = async () => {
    setOrgLoading(true);
    try {
      const response = await APIServices.get(`/organizations?type=active&status=true`); // Adjust the URL as necessary
      if (response.organizations) {
        // Map the response to match the react-select option format
        const orgOptions = response.organizations.map((org) => ({
          label: org.name, // Display name as label
          value: org._id // Use _id as value
        }));
        setOrganizations(orgOptions);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setOrgLoading(false);
    }
  };

  // Fetch organizations from API
  useEffect(() => {
    if (canLoadProjectDependencies) {
      fetchOrganizations();
    }
  }, [fetchOrgs, canLoadProjectDependencies]);

  useEffect(() => {
    if (canLoadProjectDependencies) {
      setPermissionsLoading(true);
      fetchPermissions(setPermissions).finally(() => setPermissionsLoading(false));
    }
  }, [canLoadProjectDependencies]);

  useEffect(() => {
    if (projectIdFromParams && currentTab == 5) {
      setMilestoneTab('add');
    }
  }, [currentTab]);

  useEffect(() => {
    if (isContractUploaded) {
      setMilestoneTab('add');
    }
  }, [isContractUploaded]);

  useEffect(() => {
    if (projectWithContract) {
      setWithContract(projectWithContract);
      setCurrentTab(4);
    }
  }, [projectWithContract]);

  const handleBackClick = () => {
    if (projectIdFromParams) {
      navigate(`/project-board/project/${projectIdFromParams}`, {
        state: {
          // Ensure Kanban opens with Overview tab selected.
          boardViewType: 'overview',
          isProjectRelatedBoard: true,
          projectId: projectIdFromParams,
          isFromHome: true
        }
      }); // This goes back to the previous page
    } else if (currentTab == 1) {
      if (projectIdFromParams) {
        navigate(`/project-board/project/${projectIdFromParams}`, {
          state: {
            // Ensure Kanban opens with Overview tab selected.
            boardViewType: 'overview',
            isProjectRelatedBoard: true,
            projectId: projectIdFromParams,
            isFromHome: true
          }
        }); // This goes back to the previous page
      } else {
        navigate('/'); // This goes back to the previous page
      }
    } else if (currentTab == 5 && milestoneProgress != null && !projectIdFromParams && !isContractUploaded) {
      setMilestoneTab(null);
    } else {
      setCurrentTab(currentTab - 1);
    }
  };

  const toggleModal = (isOpen) => {
    setModalOpen(isOpen);
  };

  const HeaderContent = (
    <>
      <h2>Overview</h2>
      <div className="head-right">
        {/* <a>
          <img
            src={settingIcon}
            alt="menu"
            className="feather-img menuicon"
            onClick={() => {
              navigate("/settings");
            }} // Sidebar toggle handler
          />
        </a> */}
        <a onClick={handleBackClick}>
          <img src={leftArrow} alt="comp1" className="left-arrow" />
        </a>
      </div>
    </>
  );

  // useEffect(() => {
  //   if (projectDetails) {
  //     console.log(projectDetails);
  //   }
  // }, [projectDetails]);

  const closeAndUpdateList = () => {
    toggleModal(false);
    setFetchOrgs((prevState) => !prevState);
  };

  if (!canLoadProjectDependencies) {
    return (
      <div className="wrapper create_project">
        <Header content={HeaderContent} />
        <div className="main-content">
          {projectAccessStatus === 'checking' ? (
            <div className="nodata-loader project-access-loader">{selectLoader(70)}</div>
          ) : (
            <div className="project-access-denied">
              <div className="project-access-denied-content">
                <img className="akarFile" src={akarFile} alt="Project access unavailable" />
                <h2>You don’t currently have access to this project.</h2>
                <p>Please contact the project administrator to request access.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper create_project">
      <Header content={HeaderContent} />
      <div className="main-content">
        <section className="overview-sec">
          <div className="container">
            <div className="row align-items-start">
              <div className="col-lg-12 col-md-12">
                <ul className="nav nav-pills" id="pills-tab" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${currentTab == 1 ? 'active' : ''}`}
                      id="pills-updated-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#pills-updated"
                      type="button"
                      role="tab"
                      aria-controls="pills-updated"
                      aria-selected="false"
                      onClick={() => {
                        setCurrentTab(1);
                      }}>
                      Details
                    </button>
                  </li>

                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${currentTab == 2 ? 'active' : ''}`}
                      id="pills-feedback-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#pills-feedback"
                      type="button"
                      role="tab"
                      aria-controls="pills-feedback"
                      aria-selected="true"
                      onClick={() => {
                        setCurrentTab(2);
                      }}
                      disabled={!completedTabs[2]}>
                      Team
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${currentTab == 3 ? 'active' : ''}`}
                      id="pills-updating-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#pills-updating"
                      type="button"
                      role="tab"
                      aria-controls="pills-updating"
                      aria-selected="true"
                      onClick={() => {
                        setCurrentTab(3);
                      }}
                      disabled={!completedTabs[3]}>
                      Updates
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${currentTab == 4 ? 'active' : ''}`}
                      id="pills-requests-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#pills-requests"
                      type="button"
                      role="tab"
                      aria-controls="pills-requests"
                      aria-selected="true"
                      onClick={() => {
                        setCurrentTab(4);
                      }}
                      disabled={!completedTabs[4]}>
                      Scope of Work
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${currentTab == 5 ? 'active' : ''}`}
                      id="pills-work-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#pills-work"
                      type="button"
                      role="tab"
                      aria-controls="pills-work"
                      aria-selected="true"
                      onClick={() => {
                        if (!projectIdFromParams && !isContractUploaded) {
                          setMilestoneTab(null);
                        }
                        setCurrentTab(5);
                      }}
                      disabled={!completedTabs[5]}>
                      Milestones
                    </button>
                  </li>
                </ul>

                <div className="tab-content " id="pills-tabContent">
                  <div
                    className={`tab-pane fade ${currentTab == 1 ? 'active show' : ''}`}
                    id="pills-updated"
                    role="tabpanel"
                    aria-labelledby="pills-updated-tab">
                    <DetailsTab
                      isInternalProject={isInternalProject}
                      setInternalProject={setInternalProject}
                      currentTab={currentTab}
                      addedOrgId={addedOrgId}
                      openModal={() => toggleModal(true)}
                      setCurrentTab={setCurrentTab}
                      setCompletedTabs={setCompletedTabs}
                      setprojectDetails={setprojectDetails}
                      organizationsList={organizations}
                      orgloading={orgloading}
                      setProjectStatus={setProjectStatus}
                      setEditProjectDetails={setEditProjectDetails}
                      // initialProjectData={projectDetails}
                      contractProjectId={projectDetails._id}
                      initialProjectData={contractPrefill.project}
                      prefetchedProjectDetails={prefetchedProjectDetails}
                    />
                  </div>

                  <div
                    className={`tab-pane fade ${currentTab == 2 ? 'active show' : ''}`}
                    id="pills-feedback"
                    role="tabpanel"
                    aria-labelledby="pills-feedback-tab">
                    <TeamTab
                      isInternalProject={isInternalProject}
                      currentTab={currentTab}
                      projectDetails={projectDetails}
                      setCurrentTab={setCurrentTab}
                      setCompletedTabs={setCompletedTabs}
                      organizationsList={organizations}
                      setDefaultTeam={setDefaultTeam}
                      permissions={userPermissions}
                      permissionsLoading={permissionsLoading}
                    />
                  </div>
                  <div
                    className={`tab-pane fade ${currentTab == 3 ? 'active show' : ''}`}
                    id="pills-updating"
                    role="tabpanel"
                    aria-labelledby="pills-updating-tab">
                    <UpdatesTab
                      isInternalProject={isInternalProject}
                      currentTab={currentTab}
                      projectDetails={projectDetails}
                      setCurrentTab={setCurrentTab}
                      setCompletedTabs={setCompletedTabs}
                      // organizationsList={organizations}
                    />
                  </div>

                  <div
                    className={`tab-pane fade ${currentTab == 4 ? 'active show' : ''}`}
                    id="pills-requests"
                    role="tabpanel"
                    aria-labelledby="pills-requests-tab">
                    <div className="card-body-content">
                      <ScopeOfWork
                        setprojectDetails={setprojectDetails}
                        projectDetails={projectDetails}
                        currentTab={currentTab}
                        setCurrentTab={setCurrentTab}
                        setCompletedTabs={setCompletedTabs}
                        isWithContract={isWithContract}
                        organizationsList={organizations}
                        setInternalProject={setInternalProject}
                        isInternalProject={isInternalProject}
                        editProjectDetails={editProjectDetails}
                        defaultTeam={defaultTeam}
                        permissions={userPermissions}
                        contractPrefill={contractPrefill}
                        setContractPrefill={setContractPrefill}
                        setWithContract={setWithContract}
                        setContractUploaded={setContractUploaded}
                      />
                    </div>
                  </div>

                  <div
                    className={`tab-pane fade ${currentTab == 5 ? 'active show' : ''}`}
                    id="pills-work"
                    role="tabpanel"
                    aria-labelledby="pills-work-tab">
                    <div className="card-body-content">
                      <MileStone
                        currentTab={currentTab}
                        projectDetails={projectDetails}
                        milestoneProgress={milestoneProgress}
                        setMilestoneTab={setMilestoneTab}
                        setProjectStatus={setProjectStatus}
                        editProjectDetails={editProjectDetails}
                        isWithContract={isWithContract}
                        contractMilestones={contractPrefill.milestones}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Modal Component */}
        <SidebarModal
          extraClass={'edit-profile-modal client_modal'}
          show={modalOpen}
          onHide={() => toggleModal(false)}
          headerTitle={'Add New Organization'}>
          <OrganizationForm
            setOrgId={setOrgId}
            // initialData={editingOrg}
            closeModal={closeAndUpdateList}
          />
        </SidebarModal>
      </div>
    </div>
  );
};

export default ProjectOverview;
